import { useI18n } from "vue-i18n";

import {
  type InfiniteData,
  useInfiniteQuery,
  useMutation,
} from "@tanstack/vue-query";

import { AgentFeatureType, type AgentVisibilityType } from "@/enums/ai";
import { getUpdatedVisibilities } from "@/helpers/agents";
import { useNotification } from "@/hooks/notifications";
import { queryClient } from "@/query/queryClient";
import { aiAgentsApiService } from "@/services/modules/aiAgents";

import type {
  AiAgentBase,
  UpdateAgentsVisibilityPayload,
} from "@/models/aiAgents";
import type { IPaginatedResponse } from "@/models/common";
import type { Ref } from "vue";

const QUERY_KEYS = {
  agents: (search: Ref<string>) => ["aiAgents", "infinite", search],
} as const;

export const useAgentsInfiniteQuery = (params: {
  search: Ref<string>;
  [key: string]: unknown;
}) => {
  return useInfiniteQuery<
    IPaginatedResponse<AiAgentBase>,
    Error,
    InfiniteData<IPaginatedResponse<AiAgentBase>>,
    (string | number)[],
    number
  >({
    queryKey: QUERY_KEYS.agents(params.search),
    queryFn: async ({ pageParam = 1 }) => {
      const queryParams = {
        ...params,
        search: params.search.value,
        page: pageParam,
      };
      return await aiAgentsApiService.getAll(queryParams);
    },
    getNextPageParam: (lastPage) => {
      const { current_page, last_page } = lastPage.meta;
      return current_page < last_page ? current_page + 1 : undefined;
    },
    initialPageParam: 1,
    gcTime: 0,
    staleTime: 0,
  });
};

export const useAgentVisibilityMutation = () => {
  const { t } = useI18n();
  const { showMessage } = useNotification();

  return useMutation({
    mutationFn: ({
      payload,
      role,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      search,
    }: {
      payload: UpdateAgentsVisibilityPayload;
      role: AgentVisibilityType;
      search: Ref<string>;
    }): Promise<void> =>
      aiAgentsApiService.updateAgentsVisibility({
        role,
        payload,
      }),
    onMutate: async (updatedVisibility) => {
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({
        queryKey: QUERY_KEYS.agents(updatedVisibility.search),
      });

      // Snapshot the previous value
      const previousSettings = queryClient.getQueryData(
        QUERY_KEYS.agents(updatedVisibility.search)
      );

      // Optimistically update to the new value
      queryClient.setQueryData(
        QUERY_KEYS.agents(updatedVisibility.search),
        (prevSettings: InfiniteData<IPaginatedResponse<AiAgentBase>>) => ({
          ...prevSettings,
          pages:
            prevSettings?.pages?.map((page) => ({
              ...page,
              data: page.data.map((agent) => {
                const updatedVisibilityType = updatedVisibility.role;
                const { id, is_visible } =
                  updatedVisibility.payload.ai_agents[0];

                if (agent.id !== id) {
                  return agent;
                }

                return {
                  ...agent,
                  visibilities: getUpdatedVisibilities(
                    agent.visibilities ?? [],
                    {
                      agent_id: id,
                      is_visible,
                      applies_to_type: updatedVisibilityType,
                      feature: AgentFeatureType.CommandBarAgentSearch,
                    }
                  ),
                };
              }),
            })) ?? [],
        })
      );

      return { previousSettings };
    },
    onSettled: (data, error, variables) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.agents(variables.search),
      });
    },
    onError: (err, updatedSettings, context) => {
      queryClient.setQueryData(
        QUERY_KEYS.agents(updatedSettings.search),
        context?.previousSettings
      );
      showMessage(t("ERRORS.AGENT_VISIBILITY_UPDATE_ERROR"), "error");
    },
  });
};
